import { BaseService } from '@common/http-layer/service/base.service'
import { ChannelMembershipType } from '../types'
import type {
  ChannelMetadata,
  ChannelRoles,
  MoveChannelPlacementInput,
  ChannelSequenceItem,
  ChannelVisibility,
  CreateChannelInput,
  IChannel,
  IChannelMember,
  IChannelMemberComposit,
  IChannelStatsResponse,
  PaginationParams,
  JoinPaidChannelInput,
  UpdateChannelAccessSettingsInput,
  UpdateChannelInput,
  UpdateChannelMembershipSettingsInput,
} from '../types'

/**
 * Channels service - handles all channel-related API calls
 * Base URL: COMMUNITIES_SERVER_URL
 * Base Path: /{locationId}/groups/{groupId}/channels
 */
export class ChannelsService extends BaseService {
  constructor() {
    super({
      baseURL: 'COMMUNITIES_SERVER_URL',
      basePath: '', // We'll build the path dynamically with locationId and groupId
    })
  }

  /**
   * Get location and group-aware base path
   */
  private getBasePath(locationId: string, groupId: string): string {
    if (!locationId) throw new Error('Location ID is required')
    if (!groupId) throw new Error('Group ID is required')

    return `/${locationId}/groups/${groupId}/channels`
  }

  private normalizeChannel(rawChannel: IChannel & { id?: string }): IChannel {
    return {
      ...rawChannel,
      _id: rawChannel._id || rawChannel.id,
    }
  }

  private normalizeChannels(rawChannels: Array<IChannel & { id?: string }> | undefined): IChannel[] {
    if (!rawChannels?.length) {
      return []
    }
    return rawChannels.map(channel => this.normalizeChannel(channel))
  }

  /**
   * Create a new channel
   */
  async create(data: CreateChannelInput & { groupId: string; locationId: string }): Promise<IChannel> {
    const groupPath = this.getBasePath(data.locationId, data.groupId)
    const { data: responseData } = await this.post(groupPath, data, {
      groupId: data.groupId,
      locationId: data.locationId,
    })
    return this.normalizeChannel(responseData)
  }

  /**
   * Update an existing channel
   */
  async update(data: UpdateChannelInput & { groupId: string; locationId: string }): Promise<IChannel> {
    const groupPath = this.getBasePath(data.locationId, data.groupId)
    const { data: responseData } = await this.patch<IChannel>(`${groupPath}/${data._id}`, data, {
      groupId: data.groupId,
      locationId: data.locationId,
    })
    return this.normalizeChannel(responseData)
  }

  /**
   * Delete a channel
   */
  async deleteChannel(channelId: string, groupId: string, locationId: string): Promise<void> {
    const groupPath = this.getBasePath(locationId, groupId)
    await this.delete(`${groupPath}/${channelId}`, {
      groupId,
      locationId,
    })
  }

  /**
   * Fetch all channels for a group
   */
  async fetchAllChannels(groupId: string, locationId: string, noCache?: boolean): Promise<IChannel[]> {
    const groupPath = this.getBasePath(locationId, groupId)
    const headers: Record<string, string> = noCache ? { 'Cache-Control': 'no-cache' } : {}
    const { data } = await this.get<IChannel[]>(groupPath, {
      headers,
      groupId,
      locationId,
    })
    return this.normalizeChannels(data)
  }

  /**
   * Fetch channel metadata (managers, unread counts, etc.)
   */
  async fetchChannelsMetadata(groupId: string, locationId: string): Promise<ChannelMetadata> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get<ChannelMetadata>(`${groupPath}/metadata`, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Get channel by slug
   */
  async getChannelBySlug(slug: string, groupId: string, locationId: string): Promise<IChannel> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get<IChannel>(`${groupPath}/slug/${slug}`, {
      groupId,
      locationId,
    })
    if (!data) return data as IChannel
    return this.normalizeChannel(data)
  }

  /**
   * Get channel by ID
   */
  async getChannelById(channelId: string, groupId: string, locationId: string): Promise<IChannel> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get<IChannel>(`${groupPath}/${channelId}`, {
      groupId,
      locationId,
    })
    if (!data) return data as IChannel
    return this.normalizeChannel(data)
  }

  /**
   * Fetch channel stats (posts, members, admin/manager counts)
   */
  async getChannelStats(channelId: string, groupId: string, locationId: string): Promise<IChannelStatsResponse> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get<IChannelStatsResponse>(`${groupPath}/${channelId}/stats`, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Fetch channel members
   */
  async fetchChannelMembers(
    channelId: string,
    groupId: string,
    locationId: string,
    roles?: string,
    paginationParams?: PaginationParams,
    searchText?: string
  ): Promise<IChannelMemberComposit[]> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.get<IChannelMemberComposit[]>(`${groupPath}/${channelId}/users`, {
      params: {
        roles,
        searchText,
        ...paginationParams,
      },
      groupId,
      locationId,
    })
    return data || []
  }

  /**
   * Add members to a channel
   */
  async addChannelMembers(
    membersData: Array<{ contactId: string; role: ChannelRoles; notify: boolean }>,
    channelId: string,
    groupId: string,
    locationId: string
  ): Promise<IChannelMember[]> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.post<IChannelMember[]>(`${groupPath}/${channelId}/users`, membersData, {
      groupId,
      locationId,
    })
    return data || []
  }

  /**
   * Update member role in a channel
   */
  async updateMemberRole(channelId: string, contactId: string, role: ChannelRoles, groupId: string, locationId: string): Promise<any> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.patch(`${groupPath}/${channelId}/users/${contactId}?updatedRole=${role}`, undefined, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Remove user from channel
   */
  async removeUserFromChannel(channelId: string, contactId: string, groupId: string, locationId: string): Promise<any> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.delete(`${groupPath}/${channelId}/users/${contactId}`, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Leave channel
   */
  async leaveChannel(channelId: string, groupId: string, locationId: string): Promise<any> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.delete(`${groupPath}/${channelId}/users/leave-channel`, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Update channel membership pricing (private channels only)
   */
  async updateMembershipSettings(
    data: UpdateChannelMembershipSettingsInput & { groupId: string; locationId: string }
  ): Promise<IChannel> {
    const groupPath = this.getBasePath(data.locationId, data.groupId)
    const body =
      data.membershipType === ChannelMembershipType.PAID
        ? { membershipType: data.membershipType, prices: data.prices, isLivePayment: data.isLivePayment !== false }
        : { membershipType: data.membershipType }

    const { data: responseData } = await this.patch<IChannel>(`${groupPath}/${data.channelId}/membership-settings`, body, {
      groupId: data.groupId,
      locationId: data.locationId,
    })
    return this.normalizeChannel(responseData)
  }

  /**
   * Update channel access settings (visibility and discoverability)
   */
  async updateAccessSettings(
    data: UpdateChannelAccessSettingsInput & { groupId: string; locationId: string }
  ): Promise<IChannel> {
    const groupPath = this.getBasePath(data.locationId, data.groupId)
    const { data: responseData } = await this.patch<IChannel>(
      `${groupPath}/${data.channelId}/access-settings`,
      {
        visibility: data.visibility,
        isDiscoverable: data.isDiscoverable,
      },
      {
        groupId: data.groupId,
        locationId: data.locationId,
      }
    )
    return this.normalizeChannel(responseData)
  }

  /**
   * Update channel visibility
   */
  async updateChannelVisibility(channelId: string, visibility: ChannelVisibility, groupId: string, locationId: string): Promise<any> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.patch(`${groupPath}/${channelId}/update-visibility?visibility=${visibility}`, undefined, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Join a paid private channel after checkout completes.
   */
  async joinPaidChannel(
    channelId: string,
    groupId: string,
    locationId: string,
    payload: JoinPaidChannelInput
  ): Promise<IChannelMember> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.post<IChannelMember>(`${groupPath}/${channelId}/join`, payload, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Mark channel as read
   */
  async markChannelAsRead(channelId: string, groupId: string, locationId: string): Promise<any> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.post(`${groupPath}/${channelId}/mark-as-read`, undefined, {
      groupId,
      locationId,
    })
    return data
  }

  /**
   * Update channel sequence for a group
   * Passes x-group-id and x-location-id (via options) for GoKollab
   */
  async updateChannelSequence(groupId: string, locationId: string, updatedChannelSequence: ChannelSequenceItem[]): Promise<any> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.put(
      `${groupPath}/update-channel-sequence`,
      {
        updatedChannelSequence,
      },
      {
        groupId,
        locationId,
      }
    )
    return data
  }

  /**
   * Move a channel within root/section placement using visible sibling anchors
   */
  async moveChannel(groupId: string, locationId: string, placement: MoveChannelPlacementInput): Promise<Record<string, unknown>> {
    const groupPath = this.getBasePath(locationId, groupId)
    const { data } = await this.patch(
      `${groupPath}/${placement.channelId}/move`,
      {
        targetSectionId: placement.targetSectionId,
        prevSiblingId: placement.prevSiblingId ?? null,
        nextSiblingId: placement.nextSiblingId ?? null,
      },
      {
        groupId,
        locationId,
      }
    )
    return data as Record<string, unknown>
  }

  /**
   * Get channel by location (alias for fetchAllChannels)
   */
  async getChannelsByLocation(groupId: string, locationId: string, _query?: PaginationParams): Promise<IChannel[]> {
    return this.fetchAllChannels(groupId, locationId, false)
  }

  /**
   * Get group members for channel suggestions
   * Special endpoint that returns users who can be added to a specific channel
   */
  async getGroupMembersForChannelSuggestions(
    groupId: string,
    locationId: string,
    channelId: string,
    params?: {
      limit?: number
      skip?: number
      search?: string
    }
  ): Promise<IChannelMemberComposit[]> {
    try {
      // Convert skip to pageNo (API uses pageNo, not skip)
      const pageNo = params?.skip ? Math.floor(params.skip / (params.limit || 10)) + 1 : 1

      const queryParams = {
        searchText: params?.search || '',
        limit: params?.limit || 10,
        pageNo,
      }

      const { data } = await this.get<IChannelMemberComposit[]>(`/${locationId}/groups/${groupId}/users/channel-suggestions/${channelId}`, {
        params: queryParams,
        groupId,
        locationId,
      })

      return data || []
    } catch (error) {
      console.error(`[ChannelsService.getGroupMembersForChannelSuggestions] ❌ Error fetching channel suggestions:`, error)
      throw error
    }
  }
}
